import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

export const cvManagementApi = {
  // Education Degrees
  getAllEducationDegrees: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/education-degrees`);
    } catch (error) {
      throw new ApiError('Failed to fetch education degrees', error.status || 500);
    }
  },

  getEducationDegreeById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/education-degrees/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Education degree not found', 404);
      }
      throw new ApiError('Failed to fetch education degree', error.status || 500);
    }
  },

  createEducationDegree: async (educationDegree) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/education-degrees`, educationDegree);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid education degree data', 400);
      }
      throw new ApiError('Failed to create education degree', error.status || 500);
    }
  },

  updateEducationDegree: async (id, educationDegree) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/cv/configuration/education-degrees/${id}`, educationDegree);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid education degree data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Education degree not found', 404);
      }
      throw new ApiError('Failed to update education degree', error.status || 500);
    }
  },

  deleteEducationDegree: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/cv/configuration/education-degrees/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Education degree not found', 404);
      }
      throw new ApiError('Failed to delete education degree', error.status || 500);
    }
  },

  // Languages
  getAllLanguages: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/languages`);
    } catch (error) {
      throw new ApiError('Failed to fetch languages', error.status || 500);
    }
  },

  getLanguageById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/languages/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Language not found', 404);
      }
      throw new ApiError('Failed to fetch language', error.status || 500);
    }
  },

  createLanguage: async (language) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/languages`, language);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid language data', 400);
      }
      throw new ApiError('Failed to create language', error.status || 500);
    }
  },

  updateLanguage: async (id, language) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/cv/configuration/languages/${id}`, language);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid language data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Language not found', 404);
      }
      throw new ApiError('Failed to update language', error.status || 500);
    }
  },

  deleteLanguage: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/cv/configuration/languages/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Language not found', 404);
      }
      throw new ApiError('Failed to delete language', error.status || 500);
    }
  },

  // Language Levels
  getAllLanguageLevels: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/language-levels`);
    } catch (error) {
      throw new ApiError('Failed to fetch language levels', error.status || 500);
    }
  },

  getLanguageLevelById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/language-levels/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Language level not found', 404);
      }
      throw new ApiError('Failed to fetch language level', error.status || 500);
    }
  },

  createLanguageLevel: async (languageLevel) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/language-levels`, languageLevel);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid language level data', 400);
      }
      throw new ApiError('Failed to create language level', error.status || 500);
    }
  },

  updateLanguageLevel: async (id, languageLevel) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/cv/configuration/language-levels/${id}`, languageLevel);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid language level data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Language level not found', 404);
      }
      throw new ApiError('Failed to update language level', error.status || 500);
    }
  },

  deleteLanguageLevel: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/cv/configuration/language-levels/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Language level not found', 404);
      }
      throw new ApiError('Failed to delete language level', error.status || 500);
    }
  },

  // Skills Reconciliation
  getAllSkills: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/skills`);
    } catch (error) {
      throw new ApiError('Failed to fetch skills', error.status || 500);
    }
  },

  getCustomSkills: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/custom-skills`);
    } catch (error) {
      throw new ApiError('Failed to fetch custom skills', error.status || 500);
    }
  },

  getSkillById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/skills/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Skill not found', 404);
      }
      throw new ApiError('Failed to fetch skill', error.status || 500);
    }
  },

  createSkill: async (skill) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/skills`, skill);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid skill data', 400);
      }
      throw new ApiError('Failed to create skill', error.status || 500);
    }
  },

  updateSkill: async (id, skill) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/cv/configuration/skills/${id}`, skill);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid skill data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Skill not found', 404);
      }
      throw new ApiError('Failed to update skill', error.status || 500);
    }
  },

  deleteSkill: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/cv/configuration/skills/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Skill not found', 404);
      }
      throw new ApiError('Failed to delete skill', error.status || 500);
    }
  },

  reconcileSkill: async (customSkillId, radarSkillId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/custom-skills/${customSkillId}/reconcile`, {
        radarSkillId
      });
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Custom skill not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid reconciliation data', 400);
      }
      throw new ApiError('Failed to reconcile skill', error.status || 500);
    }
  },

  deleteCustomSkill: async (customSkillId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/custom-skills/${customSkillId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError('Custom skill ID is required', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Custom skill not found', 404);
      }
      if (error.status === 403) {
        throw new ApiError('You do not have permission to delete this custom skill', 403);
      }
      throw new ApiError(error.data?.error || 'Failed to delete custom skill', error.status || 500);
    }
  },

  // Certification Issuers
  getAllCertificationIssuers: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/certification-issuers`);
    } catch (error) {
      throw new ApiError('Failed to fetch certification issuers', error.status || 500);
    }
  },

  getCustomCertificationIssuers: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/custom-certification-issuers`);
    } catch (error) {
      throw new ApiError('Failed to fetch custom certification issuers', error.status || 500);
    }
  },

  getGeneralCertificationIssuers: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/certification-issuers/general`);
    } catch (error) {
      throw new ApiError('Failed to fetch general certification issuers', error.status || 500);
    }
  },

  getEmployeeCertificationIssuers: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/certification-issuers/employee/${employeeId}`);
    } catch (error) {
      throw new ApiError(`Failed to fetch certification issuers for employee ${employeeId}`, error.status || 500);
    }
  },

  getCertificationIssuerById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/certification-issuers/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Certification issuer not found', 404);
      }
      throw new ApiError('Failed to fetch certification issuer', error.status || 500);
    }
  },

  createCertificationIssuer: async (issuer) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/certification-issuers`, issuer);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid certification issuer data', 400);
      }
      throw new ApiError('Failed to create certification issuer', error.status || 500);
    }
  },

  updateCertificationIssuer: async (id, issuer) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/cv/configuration/certification-issuers/${id}`, issuer);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid certification issuer data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Certification issuer not found', 404);
      }
      throw new ApiError('Failed to update certification issuer', error.status || 500);
    }
  },

  deleteCertificationIssuer: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/cv/configuration/certification-issuers/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Certification issuer not found', 404);
      }
      throw new ApiError('Failed to delete certification issuer', error.status || 500);
    }
  },

  reconcileCertificationIssuer: async (customIssuerId, standardIssuerId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/certification-issuers/${customIssuerId}/reconcile`, {
        standardIssuerId
      });
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Custom certification issuer not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid reconciliation data', 400);
      }
      throw new ApiError('Failed to reconcile certification issuer', error.status || 500);
    }
  },

  // Custom Projects Reconciliation
  getCustomProjects: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/custom-projects`);
    } catch (error) {
      throw new ApiError('Failed to fetch custom projects', error.status || 500);
    }
  },

  reconcileCustomProject: async (customProjectId, projectId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/custom-projects/${customProjectId}/reconcile`, {
        projectId
      });
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Custom project not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid reconciliation data', 400);
      }
      throw new ApiError('Failed to reconcile project', error.status || 500);
    }
  },

  // Custom Classifications Reconciliation
  getCustomClassifications: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/custom-classifications`);
    } catch (error) {
      throw new ApiError('Failed to fetch custom classifications', error.status || 500);
    }
  },

  reconcileCustomClassification: async (customClassificationId, classificationData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/custom-classifications/${customClassificationId}/reconcile`, classificationData);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Custom classification not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid reconciliation data', 400);
      }
      throw new ApiError('Failed to reconcile classification', error.status || 500);
    }
  },

  // Certification Categories
  getAllCertificationCategories: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/certification-categories`);
    } catch (error) {
      throw new ApiError('Failed to fetch certification categories', error.status || 500);
    }
  },

  getCertificationCategoryById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/certification-categories/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Certification category not found', 404);
      }
      throw new ApiError('Failed to fetch certification category', error.status || 500);
    }
  },

  createCertificationCategory: async (category) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/certification-categories`, category);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid certification category data', 400);
      }
      throw new ApiError('Failed to create certification category', error.status || 500);
    }
  },

  updateCertificationCategory: async (id, category) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/cv/configuration/certification-categories/${id}`, category);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid certification category data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Certification category not found', 404);
      }
      throw new ApiError('Failed to update certification category', error.status || 500);
    }
  },

  deleteCertificationCategory: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/cv/configuration/certification-categories/${id}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Cannot delete category that is in use', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Certification category not found', 404);
      }
      throw new ApiError('Failed to delete certification category', error.status || 500);
    }
  },

  // Certification Catalog
  getCatalogByIssuer: async (issuerId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/certification-issuers/${issuerId}/catalog`);
    } catch (error) {
      throw new ApiError('Failed to fetch certification catalog', error.status || 500);
    }
  },

  createCatalogEntry: async (issuerId, entry) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/certification-issuers/${issuerId}/catalog`, entry);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid catalog entry data', 400);
      }
      throw new ApiError('Failed to create catalog entry', error.status || 500);
    }
  },

  updateCatalogEntry: async (issuerId, id, entry) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/cv/configuration/certification-issuers/${issuerId}/catalog/${id}`, entry);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid catalog entry data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Catalog entry not found', 404);
      }
      throw new ApiError('Failed to update catalog entry', error.status || 500);
    }
  },

  deleteCatalogEntry: async (issuerId, id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/cv/configuration/certification-issuers/${issuerId}/catalog/${id}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Cannot delete catalog entry that is in use', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Catalog entry not found', 404);
      }
      throw new ApiError('Failed to delete catalog entry', error.status || 500);
    }
  },

  // Certification Name Reconciliation
  getAllCustomCertifications: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/custom-certifications`);
    } catch (error) {
      throw new ApiError('Failed to fetch custom certifications', error.status || 500);
    }
  },

  getCustomCertificationsByIssuer: async (issuerId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/configuration/certification-issuers/${issuerId}/custom-certifications`);
    } catch (error) {
      throw new ApiError('Failed to fetch custom certifications', error.status || 500);
    }
  },

  reconcileCertification: async (issuerId, customName, catalogCertificationId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/cv/configuration/certification-issuers/${issuerId}/reconcile-certification`, {
        customName,
        catalogCertificationId
      });
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Certification not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid reconciliation data', 400);
      }
      throw new ApiError('Failed to reconcile certification', error.status || 500);
    }
  },

  // Certifications Report
  getCertificationsReport: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.issuerId) { queryParams.append('issuerId', params.issuerId); }
      if (params.issuerType) { queryParams.append('issuerType', params.issuerType); }
      if (params.certificationCatalogId) { queryParams.append('certificationCatalogId', params.certificationCatalogId); }
      if (params.certificationName) { queryParams.append('certificationName', params.certificationName); }
      if (params.categoryId) { queryParams.append('categoryId', params.categoryId); }
      if (params.expirationStatus) { queryParams.append('expirationStatus', params.expirationStatus); }
      if (params.employeeId) { queryParams.append('employeeId', params.employeeId); }
      const queryString = queryParams.toString();
      return await authenticatedApi.get(`${API_BASE_URL}/reports/certifications${queryString ? `?${queryString}` : ''}`);
    } catch (error) {
      throw new ApiError('Failed to fetch certifications report', error.status || 500);
    }
  },

  getCertificationsReportByIssuer: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.issuerId) { queryParams.append('issuerId', params.issuerId); }
      if (params.issuerType) { queryParams.append('issuerType', params.issuerType); }
      if (params.certificationName) { queryParams.append('certificationName', params.certificationName); }
      if (params.categoryId) { queryParams.append('categoryId', params.categoryId); }
      if (params.expirationStatus) { queryParams.append('expirationStatus', params.expirationStatus); }
      if (params.employeeId) { queryParams.append('employeeId', params.employeeId); }
      const queryString = queryParams.toString();
      return await authenticatedApi.get(`${API_BASE_URL}/reports/certifications/by-issuer${queryString ? `?${queryString}` : ''}`);
    } catch (error) {
      throw new ApiError('Failed to fetch certifications report by issuer', error.status || 500);
    }
  },
};